/**
 * KR 연계도(Cascading) XML → 트리 파서
 *
 * AS-IS 흐름:
 *   selectKrCascadingRelMap(SQL, 평탄 preorder)
 *     → setNodeRemoveCnt(Service, 중첩 깊이 계산)
 *     → krCascading_xml.jsp(<node> 중첩 XML 생성)
 *     → SvgTree.js(d3, XML DOM 재귀 렌더)
 *
 * TO-BE 핵심:
 *   백엔드가 이미 nodeRemoveCnt로 "중첩이 끝난 XML"을 내려주므로
 *   프론트는 <node> DOM 트리를 그대로 재귀 순회만 하면 된다.
 *   (flat→tree 경로 복원 로직 불필요 — XML 중첩 구조 = 트리 그 자체)
 *
 * XML 구조 (krCascading_xml.jsp):
 *   <root>
 *     <attribute .../>                       ← PDF 설정, 무시
 *     <node id="KR_ID" title="KR명"          ← 노드(=KR)
 *           label="사용자명" color="상태색"
 *           attributes="목표^값단위#$%실적^값단위#$%달성도^값%">
 *       <node ...>...</node>                 ← 중첩 자식 (다단계 cascade)
 *     </node>
 *   </root>
 *
 * attributes 커스텀 인코딩:
 *   - 항목 구분자: '#$%'  / label-value 구분자: '^'
 *   - 계량(목표달성율): "목표^120점#$%실적^90점#$%달성도^75%"
 *   - 비계량(실행진도): "#$%#$%진척률^60%"  (앞 2개 빈 세그먼트)
 */

/** 연계도 노드의 속성 1건 (목표/실적/달성도 또는 진척률) */
export interface KrCascadingAttr {
  label: string;
  value: string;
}

/** 연계도 트리 노드 (AS-IS SvgTree 노드 박스 1개에 대응) */
export interface KrCascadingNode {
  id: string; // KR ID (scDeptId)
  krNm: string; // KR명 (title = strategyNm)
  userNm: string; // 담당자명 (label = scDeptNm = USER_NM)
  statusColor: string; // 상태 신호등 색 (color = statusColor)
  attrs: KrCascadingAttr[]; // 속성 목록 (계량: 목표/실적/달성도, 비계량: 진척률)
  children: KrCascadingNode[]; // 하위 cascade 노드
}

export const useKrCascadingTree = () => {
  /** attributes 문자열 → [{label, value}] 파싱 */
  const parseAttributes = (raw: string): KrCascadingAttr[] => {
    if (!raw) return [];
    return raw
      .split('#$%')
      .map(seg => seg.trim())
      .filter(seg => seg.length > 0) // 비계량의 앞 빈 세그먼트 제거
      .map(seg => {
        const idx = seg.indexOf('^');
        if (idx === -1) return { label: '', value: seg };
        return { label: seg.slice(0, idx), value: seg.slice(idx + 1) };
      });
  };

  /** <node> 엘리먼트 → KrCascadingNode (재귀) */
  const parseNode = (el: Element): KrCascadingNode => {
    const childNodes = Array.from(el.children).filter(
      c => c.tagName.toLowerCase() === 'node',
    );
    return {
      id: el.getAttribute('id') || '',
      krNm: el.getAttribute('title') || '',
      userNm: el.getAttribute('label') || '',
      statusColor: el.getAttribute('color') || '',
      attrs: parseAttributes(el.getAttribute('attributes') || ''),
      children: childNodes.map(parseNode),
    };
  };

  /**
   * KR 연계도 XML 문자열 → 트리 루트 노드 배열
   * @param xml fetchKrCascadingTree 응답(XML 문자열)
   * @returns 최상위 노드 배열 (연결된 KR 없으면 빈 배열)
   */
  const parseCascadingXml = (xmlInput: unknown): KrCascadingNode[] => {
    if (xmlInput == null) return [];
    let xml = typeof xmlInput === 'string' ? xmlInput : String(xmlInput);

    // (1) <root>...</root> 구간만 추출
    //  - JSP의 <%@ page %>/<%@ taglib %> 지시문이 <?xml ?> 선언 앞에 개행을
    //    남겨, application/xml 파서가 prolog 위치 오류로 전체를 실패 처리함.
    //  - <root> 시작 ~ </root> 끝만 잘라 prolog/선행 공백을 제거한다.
    const start = xml.indexOf('<root');
    const endTag = '</root>';
    const endIdx = xml.lastIndexOf(endTag);
    if (start === -1 || endIdx === -1) return [];
    xml = xml.slice(start, endIdx + endTag.length);

    // (2) 엔티티가 아닌 bare & 보정 (제목/이름에 & 포함 시 파서 실패 방지)
    xml = xml.replace(
      /&(?!(?:amp|lt|gt|quot|apos|#\d+|#x[0-9a-fA-F]+);)/g,
      '&amp;',
    );

    let doc: Document;
    try {
      doc = new DOMParser().parseFromString(xml, 'text/xml');
    } catch {
      return [];
    }
    // 파싱 실패 (잘못된 XML) 방어
    if (doc.querySelector('parsererror')) return [];

    const root = doc.documentElement;
    if (!root || root.tagName.toLowerCase() !== 'root') return [];

    return Array.from(root.children)
      .filter(c => c.tagName.toLowerCase() === 'node')
      .map(parseNode);
  };

  return { parseCascadingXml };
};
